import {
  DATA_UNAVAILABILITY_REASONS,
  SUBJECT_AREAS_OPTIONS
} from '@publishing-engine/domain/fixtures';
import {
  FormFieldDefinition,
  FormFieldSpecification
} from '@publishing-engine/domain/types/article';
import { createMarkDownLink } from '../utils/link';

export const formFieldSpecifications: Record<
  FormFieldDefinition['name'],
  FormFieldSpecification
> = {
  Title: {
    name: 'Title',
    component: 'System:Title',

    isEnabled: {
      isEditable: false,
      value: true
    },

    params: {
      validations: {
        kind: 'string',
        maxLength: { isEditable: true, value: 1000 },
        required: { isEditable: false, value: true }
      },
      labels: {
        main: { isEditable: false, value: 'Title' },
        subtitle: {
          isEditable: false,
          value:
            'A clear and informative title helps to attract the attention of potential readers.'
        }
      }
    },
    caseDataFields: {
      main: 'metadata.title'
    }
  },
  Abstract: {
    name: 'Abstract',
    component: 'System:Abstract',

    isEnabled: {
      isEditable: false,
      value: true
    },

    params: {
      validations: {
        kind: 'string',

        maxLength: { isEditable: true, value: 10000 },
        required: { isEditable: false, value: true }
      },
      labels: {
        main: { isEditable: false, value: 'Abstract' },
        subtitle: {
          isEditable: false,
          value:
            'Provide a succinct summary of your research that highlights the purpose, methodology, results, and conclusions.'
        }
      }
    },
    caseDataFields: {
      main: 'metadata.abstract'
    }
  },
  Keywords: {
    name: 'Keywords',
    component: 'System:Keywords',

    isEnabled: {
      isEditable: false,
      value: true
    },

    params: {
      validations: {
        kind: 'stringArray',
        minCountOfKeywords: { isEditable: false, value: 1 },
        maxCountOfKeywords: { isEditable: false, value: 100 },
        minLength: { isEditable: false, value: 1 },
        maxLength: { isEditable: false, value: 200 },
        required: { isEditable: false, value: true }
      },
      labels: {
        main: { isEditable: false, value: 'Keywords' },
        subtitle: {
          isEditable: false,
          value:
            'List keywords to help index your paper and make it more discoverable to your target audience.'
        }
      }
    },
    caseDataFields: {
      main: 'metadata.keywords'
    }
  },
  SubmittingAuthor: {
    name: 'SubmittingAuthor',
    component: 'System:SubmittingAuthor',

    isEnabled: {
      isEditable: false,
      value: true
    },

    params: {
      validations: {
        kind: 'author',
        orcidRequired: { isEditable: true, value: true },
        required: { isEditable: false, value: true }
      },
      labels: {
        main: { isEditable: false, value: 'Submitting Author' },
        subtitle: {
          isEditable: false,
          value:
            'Focuses on the initial submission process and ensures compliance with journal guidelines.'
        }
      }
    },
    caseDataFields: {
      main: 'submittingAuthor'
    }
  },
  Authors: {
    name: 'Authors',
    component: 'System:Authors',

    isEnabled: {
      isEditable: false,
      value: true
    },

    params: {
      validations: {
        kind: 'authors',
        minItems: { isEditable: false, value: 0 },
        required: { isEditable: false, value: true }
      },
      labels: {
        main: { isEditable: false, value: 'Co-Author Details' },
        subtitle: {
          isEditable: false,
          value:
            'Enter accurate information about each co-author including contact information which they may be contacted by.'
        }
      }
    },
    caseDataFields: {
      main: 'metadata.authors'
    }
  },
  ContactAuthor: {
    name: 'ContactAuthor',
    component: 'System:ContactAuthor',

    isEnabled: {
      isEditable: true,
      value: true
    },

    params: {
      validations: {
        kind: 'author',
        orcidRequired: { isEditable: true, value: false },
        required: { isEditable: true, value: false }
      },
      labels: {
        main: { isEditable: false, value: 'Contact Author' },
        subtitle: {
          isEditable: false,
          value:
            'This Author will manage communications with the journal during the submission and review process.'
        }
      }
    },
    caseDataFields: {
      main: 'contactAuthor'
    }
  },
  CorrespondingAuthor: {
    name: 'CorrespondingAuthor',
    component: 'System:CorrespondingAuthor',

    isEnabled: {
      isEditable: true,
      value: true
    },

    params: {
      validations: {
        kind: 'author',
        orcidRequired: { isEditable: true, value: false },
        required: { isEditable: true, value: false }
      },
      labels: {
        main: { isEditable: false, value: 'Corresponding Author' },
        subtitle: {
          isEditable: false,
          value:
            'Handles all communications post-publication, including reader inquiries and media interactions.'
        }
      }
    },
    caseDataFields: {
      main: 'correspondingAuthor'
    }
  },
  EthicsStatement: {
    name: 'EthicsStatement',
    component: 'System:EthicsStatement',

    isEnabled: {
      isEditable: true,
      value: true
    },

    params: {
      validations: {
        kind: 'dependantStringField',
        dependantField: { isEditable: false, value: 'value' },
        maxLength: { isEditable: false, value: 10000 },
        required: { isEditable: true, value: true }
      },
      labels: {
        main: {
          isEditable: false,
          value: 'Field Studies or Experiments with Humans or Animals'
        },
        subtitle: {
          isEditable: false,
          value:
            'If your research includes Field Studies or experiments with Humans or Animals, then you will be asked to provide an Ethics Statement.'
        }
      }
    },
    caseDataFields: {
      dependsOn: 'exists',
      main: 'metadata.ethicsStatement'
    }
  },
  NoveltyStatement: {
    name: 'NoveltyStatement',
    component: 'System:NoveltyStatement',

    isEnabled: {
      isEditable: true,
      value: true
    },

    params: {
      validations: {
        kind: 'nullString',
        maxLength: { isEditable: false, value: 10000 },
        required: { isEditable: true, value: true }
      },
      labels: {
        main: {
          isEditable: false,
          value: 'Novelty and Significance Statement'
        },
        subtitle: {
          isEditable: false,
          value:
            'Enter a statement to help editors and reviewers assess the novelty and significance of your research.'
        },

        section: {
          title: 'Novelty & Significance',
          explanation:
            'Clearly articulating the novelty and significance of your research helps the editorial team and reviewers understand the unique contributions of your work and its impact on the field. A well-crafted statement can highlight the relevance and potential influence of your research, facilitating a more informed and efficient review.'
        }
      }
    },
    caseDataFields: {
      main: 'metadata.noveltyStatement'
    }
  },
  Funding: {
    name: 'Funding',
    component: 'System:Funding',

    isEnabled: {
      isEditable: true,
      value: true
    },

    params: {
      validations: {
        kind: 'funding',
        required: { isEditable: true, value: true }
      },
      labels: {
        main: {
          isEditable: false,
          value: 'Funding Information'
        },
        subtitle: {
          isEditable: false,
          value:
            'Enter details of all funding sources that you and your co-authors will be acknowledging in your publication, if applicable. Select the appropriate Funder for each funding source.'
        },

        section: {
          title: 'Funder',
          explanation:
            'Funding details are sourced from the Open Funder Registry, but if your work is funded by institutions or individuals not listed in this registry, please submit them for consideration.'
        }
      }
    },
    caseDataFields: {
      dependsOn: 'exists',
      main: 'metadata.funding'
    }
  },
  FundingGeneric: {
    name: 'FundingGeneric',
    component: 'System:FundingGeneric',
    isEnabled: {
      isEditable: true,
      value: true
    },
    params: {
      validations: {
        kind: 'fundingGeneric',
        required: { isEditable: true, value: true }
      },
      labels: {
        main: {
          isEditable: false,
          value: 'Funding Information'
        },
        subtitle: {
          isEditable: false,
          value:
            'Enter details of all funding sources that you and your co-authors will be acknowledging in your publication, if applicable. Select the appropriate Funder for each funding source.'
        },
        options: {
          'self funded': {
            main: 'Self-funded'
          },
          'externally funded': {
            main: 'Externally Funded'
          },
          'not known': {
            main: 'Not known'
          }
        },
        section: {
          title: 'Funder',
          explanation:
            'Funding details are sourced from the Open Funder Registry, but if your work is funded by institutions or individuals not listed in this registry, please submit them for consideration.'
        }
      }
    },
    caseDataFields: {
      dependsOn: 'exists',
      main: 'metadata.fundingGeneric'
    }
  },
  FiguresAndTables: {
    name: 'FiguresAndTables',
    component: 'System:FiguresAndTables',

    isEnabled: {
      isEditable: true,
      value: true
    },

    params: {
      isSourceFileEnabled: { isEditable: true, value: false },
      isPermissionEvidenceEnabled: { isEditable: true, value: false },
      validations: {
        kind: 'figuresAndTables',
        required: { isEditable: true, value: false }
      },
      labels: {
        main: {
          isEditable: false,
          value: 'Figures and Tables'
        },
        subtitle: {
          isEditable: false,
          value:
            'Provide your figures and tables, along with any necessary permissions to reproduce them.'
        },

        section: {
          title: 'Does your submission contain figures and tables?',
          explanation:
            'Provide your figures and tables, along with any necessary permissions to reproduce them.'
        }
      }
    },
    caseDataFields: {
      dependsOn: 'exists',
      main: 'fileSet.FiguresAndTables'
    }
  },

  PreferenceImmediatePublication: {
    name: 'PreferenceImmediatePublication',
    component: 'System:PreferenceImmediatePublication',

    isEnabled: {
      isEditable: true,
      value: true
    },

    params: {
      validations: {
        kind: 'bool',
        required: { isEditable: true, value: true }
      },
      labels: {
        main: { isEditable: false, value: 'Immediate Publication Consent' },
        subtitle: {
          isEditable: false,
          value:
            'Opting for immediate publication accelerates the visibility of your work, allowing your research to reach the community swiftly after acceptance.'
        },
        options: {
          true: {
            main: 'Yes, I consent to immediate publication upon acceptance.',
            secondary:
              'This option is ideal for authors eager to disseminate their findings without delay.'
          },
          false: {
            main: 'No, I do not consent to immediate publication upon acceptance.',
            secondary:
              'If you have coordination needs with your institution or require a specific publication timing, you might prefer to withhold immediate consent.'
          }
        }
      }
    },
    caseDataFields: {
      main: 'preference.immediatePublication'
    }
  },
  StandardReportingGuidelines: {
    name: 'StandardReportingGuidelines',
    component: 'System:StandardReportingGuidelines',

    isEnabled: {
      isEditable: true,
      value: true
    },

    params: {
      validations: {
        kind: 'bool',
        required: { isEditable: true, value: true }
      },
      labels: {
        main: { isEditable: false, value: 'Standard Reporting Guidelines' },
        subtitle: {
          isEditable: false,
          value: `Did you follow standard reporting guidelines appropriate to your study?`
        },
        options: {
          true: {
            main: 'Yes, I confirm I have used the correct guidelines for my study.',
            secondary: `The following guidelines should be used depending on study design:\n1. ${createMarkDownLink(
              'CONSORT Statement',
              'https://www.equator-network.org/reporting-guidelines/consort/'
            )} (for reporting of randomized controlled trials)\n2. ${createMarkDownLink(
              'STARD',
              'https://www.equator-network.org/reporting-guidelines/stard/'
            )} (for reporting of diagnostic accuracy studies)\n3. ${createMarkDownLink(
              'STROBE',
              'https://www.equator-network.org/reporting-guidelines/strobe/'
            )} (for reporting of observational studies in epidemiology)\n4. ${createMarkDownLink(
              'PRISMA',
              'http://www.prisma-statement.org/'
            )} (for reporting of systematic reviews)\n5. ${createMarkDownLink(
              'MOOSE',
              'https://www.equator-network.org/reporting-guidelines/meta-analysis-of-observational-studies-in-epidemiology-a-proposal-for-reporting-meta-analysis-of-observational-studies-in-epidemiology-moose-group/'
            )} (for reporting of meta-analyses of observational studies)\n6. ${createMarkDownLink(
              'STREGA',
              'https://www.equator-network.org/reporting-guidelines/strobe-strega/'
            )} (for reporting of gene-disease association studies)\n7. ${createMarkDownLink(
              'ARRIVE',
              'https://arriveguidelines.org/'
            )} (for animal studies)`
          },
          false: {
            main: 'No, the guidelines are not applicable to my study.',
            secondary: ''
          }
        }
      }
    },
    caseDataFields: {
      main: 'preference.standardReportingGuidelines'
    }
  },
  TransparentPeerReview: {
    name: 'TransparentPeerReview',
    component: 'System:TransparentPeerReview',

    isEnabled: {
      isEditable: true,
      value: true
    },

    params: {
      validations: {
        kind: 'bool',
        required: { isEditable: true, value: true }
      },
      labels: {
        main: {
          isEditable: false,
          value: 'Do you consent to a Transparent Peer Review process?'
        },
        subtitle: {
          isEditable: false,
          value:
            "Reviewer recommendations, author responses, and editor's decision will be published alongside my article, should it be accepted."
        }
      }
    },
    caseDataFields: {
      main: 'preference.transparentPeerReview'
    }
  },
  DataAvailability: {
    name: 'DataAvailability',
    component: 'System:DataAvailability',

    isEnabled: {
      isEditable: true,
      value: true
    },

    params: {
      validations: {
        kind: 'dataAvailabilityPreference' as any,
        dependantField: { isEditable: false, value: 'value' },
        required: { isEditable: true, value: true }
      },
      labels: {
        main: {
          isEditable: false,
          value: 'Is your research data publically available?'
        },
        subtitle: {
          isEditable: false,
          value:
            'Streamline the publishing process and protect yourself from copyright infringement by identifying reproduced material in your submission'
        },
        options: {
          'openly-available': {
            main: 'The data that support the findings of this study are openly available'
          },
          'embargo-period': {
            main: 'The data that support the findings of this study will be openly available following an embargo'
          },
          'included-in-article': {
            main: 'All data that support the findings of this study are included within the article (and any supplementary files)'
          },
          'not-available': {
            main: 'No, the data supporting the findings of this study are not publicly available.'
          }
        },
        dependentOptions: DATA_UNAVAILABILITY_REASONS
      }
    },
    caseDataFields: {
      main: 'preference.dataAvailability'
    }
  },
  PublishingModel: {
    name: 'PublishingModel',
    component: 'System:PublishingModel',

    isEnabled: {
      isEditable: false,
      value: true
    },

    params: {
      validations: {
        kind: 'publishingPreference',
        required: { isEditable: false, value: true }
      },
      labels: {
        main: { isEditable: false, value: 'Publishing Model' },
        subtitle: {
          isEditable: false,
          value:
            'Select a fitting publishing model to determine how audiences will access your work.'
        },
        options: {
          openAccess: {
            main: 'Open Access',
            secondary:
              'Your research will be available to readers at no cost. Publication costs might be charged upon manuscript acceptance, and discounts or waivers can be applied depending on your country, institution or other aspects.'
          },
          subscription: {
            main: 'Subscription',
            secondary:
              'Readers only have access to your research by paying a subscription.'
          }
        },
        section: {
          title: 'Publishing Model',
          explanation:
            'You have a choice to publish your research under different models. Before your research is published, you may be asked to complete the steps that are required for the model that you choose.Your publishing preference will determine how readers access your research.'
        }
      }
    },
    caseDataFields: {
      main: 'preference.publishingModel',
      dependant: 'acknowledgeCharge'
    }
  },
  DoubleAnonymousPeerReview: {
    name: 'DoubleAnonymousPeerReview',
    component: 'System:DoubleAnonymousPeerReview',

    isEnabled: {
      isEditable: true,
      value: true
    },

    params: {
      validations: {
        kind: 'bool',
        required: { isEditable: true, value: true }
      },
      labels: {
        main: {
          isEditable: false,
          value: 'Do you consent to a Double Anonymous Peer Review process'
        },
        subtitle: {
          isEditable: false,
          value:
            "Double Anonymous Peer Review ensures that both the authors and reviewers remain unknown to each other, which is designed to remove any potential bias based on the authors' identity, affiliation, or previous work."
        }
      }
    },
    caseDataFields: {
      main: 'preference.doubleAnonymousPeerReview'
    }
  },
  Manuscript: {
    name: 'Manuscript',
    component: 'System:Manuscript',

    isEnabled: {
      isEditable: false,
      value: true
    },

    params: {
      validations: {
        kind: 'file',
        maxSizeMB: { isEditable: true, value: 25 },
        allowedFileFormats: {
          isEditable: true,
          value: {
            'application/pdf': ['.pdf'],
            'application/msword': ['.doc'],
            'application/vnd.openxmlformats': ['.docx']
          }
        },
        required: { isEditable: false, value: true }
      },
      labels: {
        main: { isEditable: false, value: 'Upload Manuscript' },
        subtitle: {
          isEditable: false,
          value:
            'Your manuscript should include all figures, tables, and supplementary material necessary for the review process'
        }
      }
    },
    caseDataFields: {
      main: 'fileSet.Manuscript'
    }
  },
  CompleteDocumentForReview: {
    name: 'CompleteDocumentForReview',
    component: 'System:CompleteDocumentForReview',

    isEnabled: {
      isEditable: false,
      value: true
    },

    params: {
      validations: {
        kind: 'file',
        maxSizeMB: { isEditable: true, value: 25 },
        allowedFileFormats: {
          isEditable: false,
          value: {
            'application/pdf': ['.pdf']
          }
        },
        required: { isEditable: false, value: false }
      },
      labels: {
        main: { isEditable: false, value: 'Complete Document for Review' },
        subtitle: {
          isEditable: false,
          value:
            'Please provide a PDF of the complete revised manuscript with highlighted/tracked changes.'
        }
      }
    },
    caseDataFields: {
      main: 'fileSet.CompleteDocumentForReview'
    }
  },
  AnonymizedManuscript: {
    name: 'AnonymizedManuscript',
    component: 'System:AnonymizedManuscript',

    isEnabled: {
      isEditable: false,
      value: true
    },

    params: {
      validations: {
        kind: 'file',
        maxSizeMB: { isEditable: true, value: 50 },
        allowedFileFormats: {
          isEditable: false,
          value: {
            'application/pdf': ['.pdf']
          }
        },
        required: { isEditable: false, value: false }
      },
      labels: {
        main: {
          isEditable: false,
          value: 'Upload your Anonymised Manuscript'
        },
        subtitle: {
          isEditable: false,
          value:
            'Ensure that your manuscript does not contain any author identifying information, including names and affiliations.'
        }
      }
    },
    caseDataFields: {
      main: 'fileSet.AnonymizedManuscript'
    }
  },
  TitlePage: {
    name: 'TitlePage',
    component: 'System:TitlePage',

    isEnabled: {
      isEditable: false,
      value: true
    },

    params: {
      validations: {
        kind: 'file',
        maxSizeMB: { isEditable: true, value: 25 },
        allowedFileFormats: {
          isEditable: true,
          value: {
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
              ['.docx']
          }
        },
        required: { isEditable: false, value: true }
      },
      labels: {
        main: {
          isEditable: false,
          value: 'Title Page'
        },
        subtitle: {
          isEditable: false,
          value:
            'Please upload your title page and include the following sections in the Title Page document only: Title, Authors and affiliations (including mail ids and ORCiDs as applicable), Corresponding Author(s), Author(s) Affiliations, Funding statement, Conflicts of Interest, Acknowledgements and Author(s) contributions.'
        }
      }
    },
    caseDataFields: {
      main: 'fileSet.TitlePage'
    }
  },
  SourceFiles: {
    name: 'SourceFiles',
    component: 'System:SourceFiles',

    isEnabled: {
      isEditable: false,
      value: true
    },

    params: {
      validations: {
        kind: 'fileArray',
        maxSizeMB: { isEditable: true, value: 50 },
        allowedFileFormats: {
          isEditable: false,
          value: {
            'application/msword': ['.doc'],
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
              ['.docx'],
            'application/x-latex': ['.tex', '.latex'],
            'image/png': ['.png'],
            'application/postscript': ['.eps'],
            'image/jpeg': ['.jpg', '.jpeg']
          } as any
        },
        required: { isEditable: false, value: false }
      },
      labels: {
        main: { isEditable: false, value: 'Source Files' },
        subtitle: {
          isEditable: false,
          value:
            'You may upload your manuscript source files to assist with processing. Accepted formats are Word (.doc/.docx) or LaTeX (.tex). For LaTeX submissions, first upload the main .tex file. If your manuscript has supporting files, click “Add Supporting .zip” and upload a ZIP containing the main .tex file together with all supporting documents. Figures may also be uploaded in standard formats (PNG, JPG, EPS).'
        }
      }
    },
    caseDataFields: {
      main: 'fileSet.SourceFiles'
    }
  },
  Supplementary: {
    name: 'Supplementary',
    component: 'System:Supplementary',

    isEnabled: {
      isEditable: false,
      value: true
    },

    params: {
      validations: {
        kind: 'fileArray',
        maxSizeMB: { isEditable: true, value: 25 },
        allowedFileFormats: {
          isEditable: false,
          value: {
            'application/pdf': ['.pdf'],
            'application/postscript': ['.eps'],
            'application/msword': ['.doc'],
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
              ['.docx'],
            'application/vnd.openxmlformats': ['.docx'],
            'application/vnd.openxmlformats-officedocument.presentationml.presentation':
              ['.pptx'],
            'application/vnd.ms-powerpoint': ['.ppt'],
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
              ['.xlsx'],
            'application/vnd.ms-excel': ['.xls'],
            'application/vnd.oasis.opendocument.text': ['.odt'],
            'application/rtf': ['.rtf'],
            'application/x-rtf': ['.rtx'],
            'image/png': ['.png'],
            'image/jpeg': ['.jpg', '.jpeg'],
            'image/tiff': ['.tif', '.tiff'],
            'image/bmp': ['.bmp'],
            'image/gif': ['.gif'],
            'image/x-emf': ['.emf'],
            'image/vnd.ms-modi': ['.mdi'],
            'application/xml': ['.xml'],
            'text/html': ['.html'],
            'application/x-tex': ['.tex'],
            'text/x-bibtex': ['.bib'],
            'application/x-latex': [
              '.tex',
              '.latex',
              '.sty',
              '.clo',
              '.cls',
              '.bst',
              '.dtx',
              '.ldf'
            ],
            'application/zip': ['.zip'],
            'application/x-rar-compressed': ['.rar'],
            'application/x-tar': ['.tar'],
            'text/plain': [
              '.txt',
              '.log',
              '.bbl',
              '.aux',
              '.out',
              '.blg',
              '.bak',
              '.fls',
              '.loc',
              '.lof',
              '.nlo',
              '.soc',
              '.thm',
              '.ins',
              '.ini'
            ],
            'text/x-tex': ['.ctex'],
            'video/mp4': ['.mp4'],
            'video/mpeg': ['.mpg'],
            'video/webm': ['.webm'],
            'video/x-msvideo': ['.avi'],
            'video/quicktime': ['.mov'],
            'video/x-ms-wmv': ['.wmv'],
            'application/octet-stream': [
              '.bin',
              '.raw',
              '.inp',
              '.sav',
              '.spl',
              '.cpi',
              '.mat',
              '.opj',
              '.vsd',
              '.dvi'
            ],
            'text/csv': ['.csv'],
            'application/x-bibtex': ['.bbx', '.brf', '.cbx'],
            'chemical/x-cif': ['.cif'],
            'application/x-dbase': ['.dbx'],
            'application/x-mathematica': ['.mtd'],
            'application/x-spss-sav': ['.sav']
          } as any
        },
        required: { isEditable: false, value: false }
      },
      labels: {
        main: { isEditable: false, value: 'Supplementary Files' },
        subtitle: {
          isEditable: false,
          value:
            'Provide additional context for your research by uploading supplementary data. This data can include extra datasets, code, multimedia files (excluding music), and other relevant materials that enhance your main submission.'
        }
      }
    },
    caseDataFields: {
      main: 'fileSet.Supplementary'
    }
  },
  AdditionalSupplementary: {
    name: 'AdditionalSupplementary',
    component: 'System:AdditionalSupplementary',

    isEnabled: {
      isEditable: false,
      value: true
    },

    params: {
      validations: {
        kind: 'fileArray',
        maxSizeMB: { isEditable: true, value: 25 },
        allowedFileFormats: {
          isEditable: false,
          value: {
            'application/pdf': ['.pdf'],
            'application/postscript': ['.eps'],
            'application/msword': ['.doc'],
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
              ['.docx'],
            'application/vnd.openxmlformats': ['.docx'],
            'application/vnd.openxmlformats-officedocument.presentationml.presentation':
              ['.pptx'],
            'application/vnd.ms-powerpoint': ['.ppt'],
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
              ['.xlsx'],
            'application/vnd.ms-excel': ['.xls'],
            'application/vnd.oasis.opendocument.text': ['.odt'],
            'application/rtf': ['.rtf'],
            'application/x-rtf': ['.rtx'],
            'image/png': ['.png'],
            'image/jpeg': ['.jpg', '.jpeg'],
            'image/tiff': ['.tif', '.tiff'],
            'image/bmp': ['.bmp'],
            'image/gif': ['.gif'],
            'image/x-emf': ['.emf'],
            'image/vnd.ms-modi': ['.mdi'],
            'application/xml': ['.xml'],
            'text/html': ['.html'],
            'application/x-tex': ['.tex'],
            'text/x-bibtex': ['.bib'],
            'application/x-latex': [
              '.tex',
              '.latex',
              '.sty',
              '.clo',
              '.cls',
              '.bst',
              '.dtx',
              '.ldf'
            ],
            'application/zip': ['.zip'],
            'application/x-rar-compressed': ['.rar'],
            'application/x-tar': ['.tar'],
            'text/plain': [
              '.txt',
              '.log',
              '.bbl',
              '.aux',
              '.out',
              '.blg',
              '.bak',
              '.fls',
              '.loc',
              '.lof',
              '.nlo',
              '.soc',
              '.thm',
              '.ins',
              '.ini'
            ],
            'text/x-tex': ['.ctex'],
            'video/mp4': ['.mp4'],
            'video/mpeg': ['.mpg'],
            'video/webm': ['.webm'],
            'video/x-msvideo': ['.avi'],
            'video/quicktime': ['.mov'],
            'video/x-ms-wmv': ['.wmv'],
            'application/octet-stream': [
              '.bin',
              '.raw',
              '.inp',
              '.sav',
              '.spl',
              '.cpi',
              '.mat',
              '.opj',
              '.vsd',
              '.dvi'
            ],
            'text/csv': ['.csv'],
            'application/x-bibtex': ['.bbx', '.brf', '.cbx'],
            'chemical/x-cif': ['.cif'],
            'application/x-dbase': ['.dbx'],
            'application/x-mathematica': ['.mtd'],
            'application/x-spss-sav': ['.sav']
          } as any
        },
        required: { isEditable: false, value: false }
      },
      labels: {
        main: {
          isEditable: false,
          value: 'Supporting Information (Supplemental File NOT for Review)'
        },
        subtitle: {
          isEditable: false,
          value:
            'The files uploaded in this section do not necessarily fall under the purview of the peer review process.'
        }
      }
    },
    caseDataFields: {
      main: 'fileSet.AdditionalSupplementary'
    }
  },
  CoverLetter: {
    name: 'CoverLetter',
    component: 'System:CoverLetter',

    isEnabled: {
      isEditable: true,
      value: true
    },

    params: {
      validations: {
        kind: 'file',
        maxSizeMB: { isEditable: true, value: 50 },
        allowedFileFormats: {
          isEditable: false,
          value: {
            'application/pdf': ['.pdf'],
            'application/msword': ['.doc'],
            'application/vnd.openxmlformats': ['.docx']
          }
        },
        required: { isEditable: true, value: false }
      },
      labels: {
        main: { isEditable: false, value: 'Cover Letter' },
        subtitle: {
          isEditable: false,
          value:
            "Enhance your submission by providing a concise cover letter that outlines your work's significance and context, aiding the editor in assessing its importance. Ensure your cover letter includes a statement of purpose, a summary of your findings, and any additional statements required by the journal."
        }
      }
    },
    caseDataFields: {
      main: 'fileSet.CoverLetter'
    }
  },
  RecommendedReviewers: {
    name: 'RecommendedReviewers',
    component: 'System:RecommendedReviewers',

    isEnabled: {
      isEditable: true,
      value: true
    },

    params: {
      validations: {
        kind: 'reviewers',
        minItems: { isEditable: false, value: 0 },
        required: { isEditable: false, value: false }
      },
      labels: {
        main: {
          isEditable: false,
          value:
            'Would you like to recommend specific reviewers for this Submission?'
        },
        subtitle: {
          isEditable: false,
          value:
            'If your expertise helps identify the best reviewers for your work, please specify which Reviewers should be included in the process, to ensure a Peer Review suited to your work.'
        },
        options: {
          true: {
            main: 'Yes'
          },
          false: {
            main: 'No'
          }
        }
      }
    },
    caseDataFields: {
      dependsOn: 'exists',
      main: 'preference.recommendedReviewers'
    }
  },
  OpposedReviewers: {
    name: 'OpposedReviewers',
    component: 'System:OpposedReviewers',

    isEnabled: {
      isEditable: true,
      value: true
    },

    params: {
      validations: {
        kind: 'reviewers',
        minItems: { isEditable: false, value: 0 },
        required: { isEditable: false, value: false }
      },
      labels: {
        main: {
          isEditable: false,
          value:
            'Would you like to oppose specific reviewers from this submission?'
        },
        subtitle: {
          isEditable: false,
          value:
            'If there are any reviewers who may have a conflict of interest or other reasons not suitable for reviewing your work, please specify which reviewers should be excluded from the process to ensure an unbiased peer review.'
        },
        options: {
          true: {
            main: 'Yes'
          },
          false: {
            main: 'No'
          }
        }
      }
    },
    caseDataFields: {
      dependsOn: 'exists',
      main: 'preference.opposedReviewers'
    }
  },
  ClinicalTrials: {
    name: 'ClinicalTrials',
    component: 'System:ClinicalTrials',

    isEnabled: {
      isEditable: true,
      value: true
    },

    params: {
      validations: {
        kind: 'dependantStringField',
        dependantField: { isEditable: false, value: 'value' },
        required: { isEditable: true, value: true }
      },
      labels: {
        main: { isEditable: false, value: 'Clinical Trials' },
        subtitle: {
          isEditable: false,
          value:
            'If your research includes any data from a Clinical Trial, then you will be asked to provide its trial registration number.'
        }
      }
    },
    caseDataFields: {
      dependsOn: 'exists',
      main: 'metadata.clinicalTrials'
    }
  },
  PreviouslySubmitted: {
    name: 'PreviouslySubmitted',
    component: 'System:PreviouslySubmitted',

    isEnabled: {
      isEditable: true,
      value: true
    },

    params: {
      validations: {
        kind: 'dependantStringField',
        dependantField: { isEditable: false, value: 'value' },
        required: { isEditable: true, value: true }
      },
      labels: {
        main: { isEditable: false, value: 'Previously Submitted' },
        subtitle: {
          isEditable: false,
          value:
            'Has this manuscript been submitted previously to this journal?'
        }
      }
    },
    caseDataFields: {
      dependsOn: 'exists',
      main: 'metadata.previouslySubmitted'
    }
  },
  ConflictOfInterestForAuthor: {
    name: 'ConflictOfInterestForAuthor',
    component: 'System:ConflictOfInterestForAuthor',

    isEnabled: {
      isEditable: true,
      value: true
    },

    params: {
      validations: {
        kind: 'dependantStringField',
        dependantField: { isEditable: false, value: 'value' },
        required: { isEditable: true, value: true }
      },
      labels: {
        main: { isEditable: false, value: 'Conflict of Interest' },
        subtitle: {
          isEditable: false,
          value: 'Do you have any conflict of interest?'
        }
      }
    },
    caseDataFields: {
      dependsOn: 'exists',
      main: 'metadata.conflictOfInterestForAuthor'
    }
  },
  AITools: {
    name: 'AITools',
    component: 'System:AITools',

    isEnabled: {
      isEditable: true,
      value: true
    },

    params: {
      validations: {
        kind: 'dependantCheckboxArray',
        dependantField: { isEditable: false, value: 'options' },
        minCountOfKeywords: { isEditable: false, value: 0 },
        maxCountOfKeywords: { isEditable: false, value: 10 },
        required: { isEditable: true, value: true }
      },
      labels: {
        main: { isEditable: false, value: 'AI Tools' },
        subtitle: {
          isEditable: false,
          value:
            'Did you use AI Tools (e.g., ChatGPT) to generate any element (including figures) within your manuscript?'
        },
        options: {
          true: {
            main: 'Yes'
          },
          false: {
            main: 'No'
          }
        },
        dependentOptions: {
          english_editing: {
            main: 'Language editing'
          },
          figure_generation: {
            main: 'Figure generation'
          },
          statistical_code: {
            main: 'Statistical code'
          },
          manuscript_generation: {
            main: 'Manuscript generation'
          }
        },
        section: {
          title: 'Select AI Tools Used',
          explanation:
            'Please select all AI tools that were used in the preparation of your manuscript.'
        }
      }
    },
    caseDataFields: {
      dependsOn: 'exists',
      main: 'metadata.aiTools'
    }
  },
  Preprint: {
    name: 'Preprint',
    component: 'System:Preprint',

    isEnabled: {
      isEditable: true,
      value: true
    },

    params: {
      validations: {
        kind: 'dependantStringField',
        dependantField: { isEditable: false, value: 'value' },
        required: { isEditable: true, value: true }
      },
      labels: {
        main: { isEditable: false, value: 'Preprint' },
        subtitle: {
          isEditable: false,
          value: 'Is this paper available on a preprint server?'
        }
      }
    },
    caseDataFields: {
      dependsOn: 'exists',
      main: 'metadata.preprint'
    }
  },
  AcceleratedPublication: {
    name: 'AcceleratedPublication',
    component: 'System:AcceleratedPublication',

    isEnabled: {
      isEditable: true,
      value: true
    },

    params: {
      validations: {
        kind: 'nullString',
        maxLength: { isEditable: false, value: 10000 },
        required: { isEditable: false, value: false }
      },
      labels: {
        main: {
          isEditable: false,
          value: 'Accelerated Publication Justification'
        },
        subtitle: {
          isEditable: false,
          value:
            'Let us know the reasons as to why this Letter should undergo Accelerated Publication.'
        }
      }
    },
    caseDataFields: {
      main: 'preference.acceleratedPublication'
    }
  },
  IPEMMembership: {
    name: 'IPEMMembership',
    component: 'System:IPEMMembership',

    isEnabled: {
      isEditable: true,
      value: true
    },

    params: {
      validations: {
        kind: 'nullString',
        maxLength: { isEditable: false, value: 10000 },
        required: { isEditable: false, value: false }
      },
      labels: {
        main: {
          isEditable: false,
          value:
            'Institute of Physics and Engineering in Medicine (IPEM) Membership'
        },
        subtitle: {
          isEditable: false,
          value:
            'To claim an IPEM Membership discount of 15%, enter your IPEM Membership number.'
        }
      }
    },
    caseDataFields: {
      main: 'preference.ipemMembership'
    }
  },
  SubjectArea: {
    name: 'SubjectArea',
    component: 'Custom:Select',

    isEnabled: {
      isEditable: true,
      value: true
    },

    params: {
      validations: {
        kind: 'options',
        options: { isEditable: true, value: SUBJECT_AREAS_OPTIONS },
        required: { isEditable: false, value: true }
      },
      labels: {
        main: {
          isEditable: false,
          value: 'Subject Area'
        },
        subtitle: {
          isEditable: false,
          value: ''
        }
      }
    },
    caseDataFields: {
      main: 'preference.subjectArea'
    }
  },
  Category: {
    name: 'Category',
    component: 'Custom:DependentTextInput',

    isEnabled: {
      isEditable: true,
      value: true
    },

    params: {
      validations: {
        kind: 'options',
        options: { isEditable: true, value: [] },
        required: { isEditable: false, value: true }
      },
      labels: {
        main: {
          isEditable: false,
          value: 'Category'
        },
        subtitle: {
          isEditable: false,
          value: ''
        }
      }
    },
    caseDataFields: {
      main: 'preference.category'
    }
  },
  FocusIssue: {
    name: 'FocusIssue',
    component: 'Custom:FocusSelect',

    isEnabled: {
      isEditable: true,
      value: true
    },

    params: {
      validations: {
        kind: 'options',
        options: { isEditable: true, value: [] },
        required: { isEditable: false, value: false }
      },
      labels: {
        main: {
          isEditable: false,
          value: 'Special Issue'
        },
        subtitle: {
          isEditable: false,
          value: ''
        }
      }
    },
    caseDataFields: {
      main: 'preference.focusIssue'
    }
  },
  SharedDataset: {
    name: 'SharedDataset',
    component: 'System:SharedDataset',

    isEnabled: {
      isEditable: true,
      value: true
    },

    params: {
      validations: {
        kind: 'dependantStringField',
        dependantField: { isEditable: false, value: 'value' },
        required: { isEditable: true, value: true }
      },
      labels: {
        main: { isEditable: false, value: 'Shared Dataset' },
        subtitle: {
          isEditable: false,
          value:
            'Is this paper a part of a number of papers from the same data set ?'
        }
      }
    },
    caseDataFields: {
      dependsOn: 'exists',
      main: 'metadata.sharedDataset'
    }
  },
  GeneratedDataset: {
    name: 'GeneratedDataset',
    component: 'System:GeneratedDataset',

    isEnabled: {
      isEditable: true,
      value: true
    },

    params: {
      validations: {
        kind: 'bool',
        required: { isEditable: true, value: true }
      },
      labels: {
        main: {
          isEditable: false,
          value: 'Generated Dataset'
        },
        subtitle: {
          isEditable: false,
          value:
            'Did your work generate any major datasets (e.g., DNA sequence data, clinical trial data, field data)?'
        }
      }
    },
    caseDataFields: {
      main: 'metadata.generatedDataset'
    }
  },
  UtilizedDataset: {
    name: 'UtilizedDataset',
    component: 'System:UtilizedDataset',

    isEnabled: {
      isEditable: true,
      value: true
    },

    params: {
      validations: {
        kind: 'bool',
        required: { isEditable: true, value: true }
      },
      labels: {
        main: {
          isEditable: false,
          value: 'Utilized Dataset'
        },
        subtitle: {
          isEditable: false,
          value:
            'Did your work use any previously published major datasets (e.g., DNA sequence data, clinical trial data, field data)?'
        }
      }
    },
    caseDataFields: {
      main: 'metadata.utilizedDataset'
    }
  },
  EthicalApproval: {
    name: 'Ethical Approval',
    component: 'System:EthicalApproval',

    isEnabled: {
      isEditable: true,
      value: true
    },

    params: {
      validations: {
        kind: 'dependantStringField',
        dependantField: { isEditable: false, value: 'value' },
        required: { isEditable: true, value: true }
      },
      labels: {
        main: { isEditable: false, value: 'Ethical Approval' },
        subtitle: {
          isEditable: false,
          value:
            'Did this project receive ethical approval from a formal institutional review board (IRB)?'
        }
      }
    },
    caseDataFields: {
      dependsOn: 'exists',
      main: 'metadata.ethicalApproval'
    }
  },
  EthicsApproval: {
    name: 'Ethics Approval',
    component: 'System:EthicsApproval',

    isEnabled: {
      isEditable: true,
      value: true
    },

    params: {
      validations: {
        kind: 'dependantStringField',
        dependantField: { isEditable: false, value: 'value' },
        required: { isEditable: true, value: true }
      },
      labels: {
        options: {
          false: {
            main: 'Yes',
            secondary: undefined
          },
          true: {
            main: 'No',
            secondary: undefined
          }
        },
        main: { isEditable: false, value: 'Ethics Approval' },
        subtitle: {
          isEditable: false,
          value:
            'Have you explicitly acknowledged this ethics approval in your manuscript, within the methods section?'
        }
      }
    },
    caseDataFields: {
      dependsOn: 'exists',
      main: 'metadata.ethicsApproval'
    }
  }
};
